<template>
  <b-modal
    ref="modal"
    :title="
      $t(isEdit ? 'breadcrumb.update_employee' : 'button.add_employee')
    "
    hide-footer
    size="lg"
    @hidden="onHidden"
  >
    <validation-observer
      ref="formValidation"
      #default="{ invalid }"
    >

      <validation-provider
        v-slot="{ errors }"
        :name="$t('breadcrumb.project')"
        rules="required"
      >
      <b-form-group
          :label="$t('breadcrumb.project')"
          label-for="projects"
        >
          <n-async-multi-select
            id="projects"
            v-model="formData.projectIds"
            name="projects"
            repository="project"
            selection-key="id"
            selection-label="translations"
            :query="{
              isActive: true,
              departmentId: $route.params.departmentId
            }"
            :get-option-label="getLabel"
            :placeholder="$t('placeholder.selectProject')"
            :init-options="isEdit ? item.project.map(project => project.id) || [] : []"
            :state="errors.length > 0 ? false : null"
            :reduce="true"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.operator')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.operatorAccount')"
          label-for="operatorAccount"
        >
          <n-async-single-select
            id="operatorAccount"
            v-model="formData.operatorId"
            name="operatorAccount"
            repository="operator"
            selection-key="id"
            :state="errors.length > 0 ? false : null"
            :label-separator="' '"
            :selection-label="['name', 'role']"
            :readonly="isEdit"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.role')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.role')"
          label-for="isManager"
        >
          <b-form-radio-group
            id="isManager"
            v-model="formData.isManager"
            :options="[
              { text: $t('field.manager'), value: true },
              { text: $t('breadcrumb.employee'), value: false }
            ]"
            buttons
            button-variant="outline-primary rounded-0"
            class="flex-wrap"
            :state="errors.length > 0 ? false : null"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.workingSchedule')"
        rules="required"
      >
        <b-form-group
          :label="$t('breadcrumb.workingSchedule')"
          label-for="workingSchedule"
        >
          <template #label>
            <div class="d-flex align-items-center">
              <span>{{ $t('breadcrumb.workingSchedule') }}</span>
              <span
                v-b-tooltip.hover.righttop.v-secondary
                class="ml-1"
                :title="$t('workingScheduleHint')"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  class="bi bi-exclamation-circle-fill"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"
                  />
                </svg>
              </span>
            </div>
          </template>
          <n-async-single-select
            id="workingSchedule"
            ref="workingSchedule"
            v-model="formData.workScheduleId"
            name="workingSchedule"
            repository="workingScheduleTemplate"
            selection-key="id"
            selection-label="translations"
            :get-option-label="getLabel"
            :is-custom-params="true"
            :state="errors.length > 0 ? false : null"
          >
            <template #list-header>
              <div
                class="d-flex align-items-center justify-content-center border-bottom cursor-pointer"
                :style="{ borderColor: '#e9ecef', padding: '8px 0' }"
                @mouseover="$refs.workingSchedule.$el.classList.add('disable-hover')"
                @mouseout="$refs.workingSchedule.$el.classList.remove('disable-hover')"
                @click.stop="handleAddWorkingSchedule"
              >
                <div
                  class="d-flex align-items-center cursor-pointer text-primary"
                >
                  <feather-icon
                    icon="PlusCircleIcon"
                    size="18"
                    class="mr-50"
                  />
                  <span class="font-weight-bolder">{{ $t('button.add') }}</span>
                </div>
              </div>
            </template>
          </n-async-single-select>
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.serviceCap')"
        rules="required"
      >
        <b-form-group label-for="serviceCap">
          <template #label>
            <div class="d-flex align-items-center">
              <span>{{ $t('field.serviceCap') }}</span>
              <span
                v-b-tooltip.hover.righttop.v-secondary
                class="ml-1"
                :title="$t('noteForServiceCap')"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  class="bi bi-exclamation-circle-fill"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"
                  />
                </svg>
              </span>
            </div>
          </template>
          <n-tel-input
            id="serviceCap"
            v-model="formData.serviceCap"
            :placeholder="$t('field.serviceCap')"
            :state="errors.length > 0 ? false : null"
            :class="formData.isBot ? 'd-none' : ''"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <validation-provider
        v-if="isEdit"
        v-slot="{ errors }"
        :name="$t('field.status')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.status')"
          label-for="isActive"
        >
          <b-form-radio-group
            id="isActive"
            v-model="formData.isActive"
            :options="[
              { text: $t('status.active'), value: true },
              { text: $t('status.inactive'), value: false }
            ]"
            buttons
            button-variant="outline-primary rounded-0"
            class="flex-wrap"
            :state="errors.length > 0 ? false : null"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <b-row class="mt-3">
        <b-col class="text-right">
          <b-button
            variant="outline-secondary"
            class="mr-2"
            @click="hide"
          >
            {{ $t('button.cancel') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="invalid || submitting"
            @click="onSubmit"
          >
            <span
              v-if="submitting"
              class="spinner-border spinner-border-sm mr-1"
            />
            {{ isEdit ? $t('button.update') : $t('button.save') }}
          </b-button>
        </b-col>
      </b-row>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BRow,
  BCol,
  BFormRadioGroup,
  BFormInvalidFeedback,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect.vue'
import NTelInput from '@/components/NTelInput.vue'
import NAsyncMultiSelect from '@/components/NAsyncMultiSelect.vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'EmployeeFormModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BRow,
    BCol,
    BFormRadioGroup,
    BFormInvalidFeedback,
    ValidationProvider,
    ValidationObserver,
    NAsyncSingleSelect,
    NTelInput,
    NAsyncMultiSelect,
    FeatherIcon,
  },
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      formData: {
        departmentId: this.$route.params.departmentId,
        operatorId: null,
        isManager: false,
        serviceCap: 1,
        workScheduleId: null,
        isActive: true,
        isBot: false,
        projectIds: [],
      },
      submitting: false,
    }
  },
  watch: {
    item: {
      handler(newVal) {
        if (this.isEdit && newVal) {
          const projectIds = newVal.project?.map(project => project.id) || []
          this.formData = {
            id: newVal.id,
            departmentId: newVal.departmentId,
            operatorId: newVal.merchantUserId,
            isManager: newVal.isManager,
            serviceCap: newVal.serviceCap,
            workScheduleId: this.item.workSchedule?.id,
            isActive: newVal.isActive,
            projectIds,
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    getLabel(option) {
      return option.title || ''
    },
    show() {
      if (this.isEdit && this.item) {
        const projectIds = this.item.project?.map(project => project.id) || []
        this.formData = {
          id: this.item.id,
          departmentId: this.item.departmentId,
          operatorId: this.item.merchantUserId,
          isManager: this.item.isManager,
          serviceCap: this.item.serviceCap,
          workScheduleId: this.item.workSchedule?.id,
          isActive: this.item.isActive,
          projectIds,
        }
      } else {
        this.resetForm()
      }
      this.$refs.modal.show()
    },
    hide() {
      this.$refs.modal.hide()
    },
    onHidden() {
      this.resetForm()
      this.$emit('hide')
    },
    resetForm() {
      this.formData = {
        departmentId: this.$route.params.departmentId,
        operatorId: null,
        isManager: false,
        serviceCap: 1,
        workScheduleId: null,
        isActive: true,
        isBot: false,
        projectIds: [],
      }
      this.$nextTick(() => {
          this.$refs.formValidation?.reset()
      })
    },
    async onSubmit() {
      const isValid = await this.$refs.formValidation.validate()

      if (!isValid || this.submitting) return

      this.submitting = true
      try {
        await this.$emit('submit', {
          ...this.formData,
          serviceCap: Number(this.formData.serviceCap) || 1,
        })
      } finally {
        this.submitting = false
      }
    },
    handleAddWorkingSchedule() {
      window.open(`/department/${this.$route.params.departmentId}/employee?tab=workingSchedule&add=true`, '_blank')
    },
  },
}
</script>

<style lang="scss" scoped>
  .modal-body {
    padding: 1.5rem;
  }

  :deep(.disable-hover) {
    .vs__dropdown-menu {
      .vs__dropdown-option--highlight {
        background: transparent !important;
        color: inherit !important;
      }
    }
  }

  :deep(.vs__dropdown-option--selected) {
    color: #7367f0 !important;
    background: rgba(115, 103, 240, 0.12) !important;
  }

  .hover-primary {
    color: #6e6b7b;
    &:hover {
      color: #7367f0;
    }
  }

  .transition-all {
    transition: all 0.15s ease;
  }
</style>
